import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { toggleMapKeyVisibility } from "@/features/auth/interactiveSurveySlice";
import type { MapKey as MapKeyType } from "@/features/auth/types";
import type { POICategory } from "@/features/poi/types";
import { getMapKeyStorageKey } from "@/shared/hooks/useMapKeyVisibility";
import { makeMapKeyCompositeKey } from "@/shared/utils/map";
import { EmeraldIcon } from "@emerald-react/icon";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router";

interface MapKeyCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const MAP_KEY_PIN_SIZE = 20;

const MapKeyCheckbox = ({ checked, onChange }: MapKeyCheckboxProps) => (
  <EmeraldIcon
    icon={checked ? "check_box" : "check_box_outline_blank"}
    type="filled"
    variant="icon"
    className="text-primary"
    onClick={() => {
      onChange(!checked);
    }}
  />
);

/**
 * Gets POI styling data (pinType, iconName, iconColor, logoUrl) for a map key
 * by looking up the POI category via referenceId (= POICategory.extId).
 */
const getPoiStylingData = (
  referenceId: string | undefined,
  categories: POICategory[],
) => {
  const category = referenceId
    ? categories.find((c) => c.extId === referenceId)
    : undefined;

  if (!category) {
    return {
      pinType: "ICON" as const,
      iconName: undefined,
      iconColor: undefined,
      logoUrl: undefined,
    };
  }

  const pinType = category.pinType ?? "ICON";
  const iconName = category.iconData?.iconName;
  const iconColor = category.iconData?.color;
  const logoUrl =
    pinType === "LOGO"
      ? (category.logoData?.logoLinks?.find((l) => l.isActive === true)?.url ??
        category.logoData?.logoLinks?.[0]?.url)
      : undefined;

  return { pinType, iconName, iconColor, logoUrl };
};

const MapKey = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const dispatch = useAppDispatch();
  const mapKeys = useAppSelector(
    (state) => state.interactiveSurvey.marketMapKeys,
  );
  const { categories, pois } = useAppSelector((state) => state.poi);
  const { mapFilters, hiddenMapKeyColors } = useAppSelector(
    (state) => state.interactiveSurvey,
  );
  const [enabledMapKeys, setEnabledMapKeys] = useState<MapKeyType[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const location = useLocation();
  const isRenderedMap = location.pathname.includes("marketmapRendered");

  useEffect(() => {
    // Build set of category IDs that have at least one non-hidden POI.
    // This mirrors the Survey Creator logic: only show POI map keys whose
    // category is actually referenced by a visible POI.
    const usedPOICategoryIds = new Set(
      pois
        .filter((p) => !p.isHidden && p.categoryId)
        .map((p) => p.categoryId!),
    );

    const isPOIMapKey = (key: MapKeyType): boolean =>
      key.category === "POI" ||
      !!key.category?.startsWith("POI_") ||
      !!key.referenceId;

    // Returns true when a key should pass the POI-usage gate.
    // Non-POI keys always pass; POI keys must have a category with active POIs.
    const hasPOIUsage = (key: MapKeyType): boolean => {
      if (!isPOIMapKey(key)) return true;
      const categoryId =
        key.referenceId ??
        categories.find((c) => c.categoryName === key.label)?.extId;
      return !!categoryId && usedPOICategoryIds.has(categoryId);
    };

    if (mapFilters.showShortlistOnly) {
      const filteredKeys = mapKeys.filter(
        (key) =>
          ((key.active && key.color.toLowerCase() === "#003f2d") ||
            (key.active && key.category !== "Property")) &&
          hasPOIUsage(key),
      );
      setEnabledMapKeys(filteredKeys);
    } else {
      setEnabledMapKeys(
        mapKeys
          .filter(
            (key) => key.active && !key?.isShortlisted && hasPOIUsage(key),
          )
          .slice(0, 8),
      );
    }
  }, [mapFilters.showShortlistOnly, mapKeys, pois, categories]);

  /**
   * Builds the composite key for a map key entry.
   * For POI keys, resolves the POICategory extId via referenceId (primary)
   * or by matching key.label to categoryName (fallback). Uses the extId as
   * the unique identifier — matching poi.categoryId in the map filter logic.
   */
  const buildCompositeKey = (key: MapKeyType): string => {
    const isPoiKey =
      key.category === "POI" ||
      key.category?.startsWith("POI_") ||
      !!key.referenceId;

    if (isPoiKey) {
      // Resolve the POICategory: prefer referenceId (extId) lookup, fall back to label match
      const poiCat = key.referenceId
        ? categories.find((c) => c.extId === key.referenceId)
        : categories.find((c) => c.categoryName === key.label);
      const iconColor = poiCat?.iconData?.color || key.color || "#6366F1";
      // Use extId as the stable identifier; fall back to referenceId then label
      const categoryId = poiCat?.extId ?? key.referenceId ?? key.label ?? "POI";
      return makeMapKeyCompositeKey(iconColor, false, categoryId);
    }

    return makeMapKeyCompositeKey(
      key.color,
      key.isCurrentLocation,
      key.category ?? "Property",
    );
  };

  const handleToggle = (key: MapKeyType) => {
    const compositeKey = buildCompositeKey(key);
    dispatch(toggleMapKeyVisibility(compositeKey));

    // Derive the next hidden list from Redux state (single source of truth),
    // mirroring the same toggle logic in the slice.
    const nextHidden = hiddenMapKeyColors.includes(compositeKey)
      ? hiddenMapKeyColors.filter((k) => k !== compositeKey)
      : [...hiddenMapKeyColors, compositeKey];

    if (!surveyId) return;
    try {
      localStorage.setItem(
        getMapKeyStorageKey(surveyId),
        JSON.stringify(nextHidden),
      );
    } catch {
      // ignore storage errors
      console.warn("Failed to persist map key visibility");
    }
  };

  return (
    <div
      className={`map-key-section${isRenderedMap ? " rendered-view" : ""}`}
      id="map-key-section"
    >
      <div
        className={`flex items-center justify-between${!isRenderedMap ? " cursor-pointer select-none" : ""}`}
        onClick={!isRenderedMap ? () => setIsExpanded((v) => !v) : undefined}
        aria-expanded={!isRenderedMap ? isExpanded : undefined}
        role={!isRenderedMap ? "button" : undefined}
        aria-label={
          !isRenderedMap
            ? isExpanded
              ? "Collapse map key"
              : "Expand map key"
            : undefined
        }
      >
        <h3 className={`map-key-title${isRenderedMap ? " rendered-view" : ""}`}>
          Map Key
        </h3>
        {!isRenderedMap && (
          <EmeraldIcon
            icon={isExpanded ? "expand_less" : "expand_more"}
            type="outlined"
            variant="icon"
          />
        )}
      </div>

      {isExpanded && (
        <ul className="mt-0.5">
          {enabledMapKeys?.map((key) => {
            const isPoiKey =
              key.category === "POI" ||
              key.category?.startsWith("POI_") ||
              !!key.referenceId;
            const compositeKey = buildCompositeKey(key);
            const isChecked = !hiddenMapKeyColors.includes(compositeKey);

            const markerAndLabel = (
              <>
                <div
                  className={`marker-pin${isPoiKey ? " marker-pin-poi" : ""}`}
                  style={{
                    color: key.color,
                  }}
                >
                  {key.category === "LeaseComp" ? (
                    <EmeraldIcon icon="square" type="filled" variant="icon" />
                  ) : isPoiKey ? (
                    (() => {
                      const styling = getPoiStylingData(
                        key.referenceId,
                        categories,
                      );
                      // For LOGO pins with valid URL, show image
                      if (styling.pinType === "LOGO" && styling.logoUrl) {
                        return (
                          <img
                            src={styling.logoUrl}
                            alt={key.label}
                            style={{
                              width: `${MAP_KEY_PIN_SIZE}px`,
                              ...(!isRenderedMap && {
                                height: `${MAP_KEY_PIN_SIZE}px`,
                              }),
                              objectFit: "contain",
                              display: "block",
                            }}
                          />
                        );
                      }
                      // For ICON pins or when logo is missing, show SVG circle with Material Icons
                      return (
                        <svg
                          width={MAP_KEY_PIN_SIZE}
                          height={MAP_KEY_PIN_SIZE}
                          viewBox="-1 -1 26 26"
                          xmlns="http://www.w3.org/2000/svg"
                          preserveAspectRatio="xMidYMid meet"
                          style={{
                            flexShrink: 0,
                            display: "block",
                            borderRadius: "50%",
                          }}
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="12"
                            stroke="#fff"
                            strokeWidth="2"
                            fill={styling.iconColor ?? key.color}
                          />
                          {styling.iconName &&
                            styling.iconName !== "circle" && (
                              <text
                                x="12"
                                y="12"
                                textAnchor="middle"
                                dominantBaseline="central"
                                style={{
                                  fontSize: "16px",
                                  fontFamily: "Material Icons",
                                  fontWeight: "400",
                                  fontStyle: "normal",
                                  fill: "#fff",
                                  pointerEvents: "none",
                                  userSelect: "none",
                                }}
                              >
                                {styling.iconName}
                              </text>
                            )}
                        </svg>
                      );
                    })()
                  ) : key.isCurrentLocation ? (
                    <EmeraldIcon icon="stars" type="filled" variant="icon" />
                  ) : (
                    <EmeraldIcon
                      style={{ color: key.color }}
                      icon="circle"
                      type="filled"
                      variant="icon"
                    />
                  )}
                </div>
                <span className="map-key-label">{key.label}</span>
              </>
            );

            return isRenderedMap ? (
              <li key={key.mapKeyId ?? compositeKey} className="map-key-item">
                {markerAndLabel}
              </li>
            ) : (
              <li key={key.mapKeyId ?? compositeKey} className="map-key-row">
                <div className="map-key-item min-w-0">{markerAndLabel}</div>
                <MapKeyCheckbox
                  checked={isChecked}
                  onChange={() => handleToggle(key)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default MapKey;
